// VIBRANIAM-SPACE v2 - mesh-shaders.js
//
// Shaders for the geometric substrate. Deliberately a separate file from
// shaders.js: the particle pipeline there is load-bearing for every existing
// preset and must not move. Nothing in here is shared with it.
//
// All the audio reaction happens in the vertex shader. The base geometry is
// uploaded once and never touched again - each frame only pushes ~70 floats of
// uniforms (the band array plus a handful of scalars), so the cost per frame is
// independent of how many vertices the shape has.

export const MESH_BANDS_MAX = 64;

export const MESH_VERT = /* glsl */ `
attribute float aBand;   // 0..1, which analysis band drives this vertex
attribute float aSeed;   // per-vertex constant, deterministic at build time

varying vec3 vColor;
varying float vAmp;

uniform float uBands[${MESH_BANDS_MAX}];
uniform float uBandCount;
uniform float uTime;

uniform float uBass;
uniform float uRms;
uniform float uHigh;
uniform float uOnsetEnv;

uniform float uDisplace;    // band energy -> displacement along the normal
uniform float uWave;        // travelling wave, audio-independent motion
uniform float uWaveSpeed;
uniform float uTwist;       // rotation around Y proportional to height
uniform float uJitter;      // highs -> per-vertex shimmer
uniform float uPulse;       // bass -> uniform scale
uniform float uSpin;        // constant rotation around Y

uniform float uHueBase;
uniform float uHueSpread;
uniform float uHueByBand;
uniform float uSaturation;
uniform float uBrightness;

float hash11(float p) {
  p = fract(p * 0.1031);
  p *= p + 33.33;
  p *= p + p;
  return fract(p);
}

vec3 hsv2rgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}

vec3 rotY(vec3 p, float a) {
  float s = sin(a), c = cos(a);
  return vec3(c * p.x + s * p.z, p.y, -s * p.x + c * p.z);
}

void main() {
  // Dynamic index into a uniform array: fine on WebGL2 (this project is
  // three r169, which is WebGL2-only).
  float bc = max(1.0, uBandCount);
  int idx = int(clamp(aBand, 0.0, 1.0) * (bc - 1.0) + 0.5);
  float amp = uBands[idx];
  vAmp = amp;

  vec3 pos = position;
  vec3 nrm = normalize(normal);

  // Band energy pushes the surface out along its own normal; the travelling
  // wave keeps the shape alive even in silence.
  float wave = sin(aBand * 12.566 - uTime * uWaveSpeed) * uWave;
  float disp = amp * uDisplace + wave + uOnsetEnv * uDisplace * 0.35;
  pos += nrm * disp;

  // Shimmer: stepped in time so it reads as a flicker, not as noise.
  float t20 = floor(uTime * 20.0);
  pos += nrm * (hash11(aSeed + t20) - 0.5) * uJitter * uHigh;

  // Twist around Y by height, plus a constant spin.
  pos = rotY(pos, pos.y * uTwist + uTime * uSpin);

  // Bass inflates the whole shape.
  pos *= 1.0 + uBass * uPulse;

  float hue = fract(uHueBase + aBand * uHueByBand + amp * uHueSpread);
  float val = uBrightness * (0.35 + amp * 1.35 + uRms * 0.6 + uOnsetEnv * 0.5);
  vColor = hsv2rgb(vec3(hue, uSaturation, max(0.0, val)));

  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}
`;

export const MESH_FRAG = /* glsl */ `
precision highp float;

varying vec3 vColor;
varying float vAmp;

uniform float uOpacity;
uniform float uGain;

void main() {
  // The layer buffer is additive and floating point, so values above 1 are
  // wanted here - that is what the bloom pass downstream picks up.
  float a = uOpacity * (0.55 + vAmp * 0.75);
  gl_FragColor = vec4(vColor * uGain * a, a);
}
`;
